import { useCallback, useState } from "react";
import { updatePortalAgreement } from "./portalRecords";
import type { PortalAgreement } from "../domain/types";

export type AgreementStatus = "pending" | "in_progress" | "completed" | "skipped";

export interface AgreementFailure {
  agreementId: string;
  kind: "status" | "comment";
}

/**
 * The patient's agreements, and the two things they can change on them.
 *
 * A status change shows at once and is taken back if the server refuses it; a
 * comment is kept only once saved. Either way a failure is reported rather
 * than swallowed: a patient who ticked something off and walked away believing
 * it was recorded is worse off than one who was told it was not.
 */
export function useAgreementUpdates() {
  const [agreements, setAgreements] = useState<PortalAgreement[]>([]);
  const [failure, setFailure] = useState<AgreementFailure | null>(null);

  const patch = (agreementId: string, changes: Partial<PortalAgreement>) =>
    setAgreements((current) =>
      current.map((agreement) =>
        agreement.id === agreementId ? { ...agreement, ...changes } : agreement
      )
    );

  const changeStatus = useCallback(
    async (agreement: PortalAgreement, status: AgreementStatus) => {
      setFailure(null);
      patch(agreement.id, { status, status_updated_at: new Date().toISOString() });
      try {
        const saved = await updatePortalAgreement(agreement.id, { status });
        patch(agreement.id, { status: saved.status });
      } catch {
        patch(agreement.id, {
          status: agreement.status,
          status_updated_at: agreement.status_updated_at,
        });
        setFailure({ agreementId: agreement.id, kind: "status" });
      }
    },
    []
  );

  const saveComment = useCallback(async (agreementId: string, comment: string) => {
    setFailure(null);
    try {
      await updatePortalAgreement(agreementId, { comment });
      patch(agreementId, { patient_comment: comment });
      return true;
    } catch {
      setFailure({ agreementId, kind: "comment" });
      return false;
    }
  }, []);

  return { agreements, setAgreements, changeStatus, saveComment, failure };
}
